import './Product.scss';

import cn from 'classnames';
import React, { useMemo, useRef } from 'react';
import Dotdotdot from 'react-dotdotdot';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import Flags from '@/common/components/atoms/Flags/Flags';
import Picture from '@/common/components/atoms/Picture/Picture';
import Tag from '@/common/components/blocks/Tag/Tag';
import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import Quantity from '@/common/components/molecules/Quantity/Quantity';
import {
  BREAKPOINTS,
  PAGE_PRODUCTS_OFFERS,
  PREDICTIONS_KEY_PARAM,
  ROUTE_PRODUCT,
} from '@/common/constants';
import { useAddToContext } from '@/common/contexts/AddToContext';
import { useAuth } from '@/common/contexts/AuthContext';
import { useDefaultCartContext } from '@/common/contexts/DefaultCartContext';
import getImageBySize from '@/common/helpers/getImageBySize';
import withPriceAndStock from '@/common/hoc/products/withPriceAndStock';
import useFlags from '@/common/hooks/flags/flags';
import { ReactComponent as CartIcon } from '@/ebat/assets/icons/basket.svg';
import ConfiguratorLink from '@/ebat/components/blocks/Common/Product/ConfiguratorLink/ConfiguratorLink';
import ProductPrice from '@/ebat/components/blocks/Common/Product/ProductPrice/ProductPrice';
import Stocks from '@/ebat/components/blocks/Common/Stocks/Stocks';
import ToggleWishlist from '@/ebat/components/blocks/Products/ToggleWishlist/ToggleWishlist';
import { PRODUCTS_TYPES_FORMATS } from '@/ebat/constants';
import { useModalIAContext } from '@/ebat/contexts/ModalIAContext';

import { ProductDefaultProps, ProductPropTypes } from './Product.proptypes';

const Product = (props) => {
  const {
    addToCartOptions,
    attributionToken,
    availableQuantity,
    brand,
    breadcrumb,
    categories,
    className,
    codeOldCompanySku,
    companyAvailability,
    configurations,
    configurator,
    customerPrice,
    delayQuantity,
    disabled,
    discountPrice,
    exclusivity,
    flags,
    hasPrice, // withPriceAndStock
    hasStock, // withPriceAndStock
    id,
    image,
    imageSize,
    invoiceUnit,
    isFavorite,
    isRelatedProduct,
    isTableRow,
    mainSku, // withPriceAndStock
    manufacturerSku,
    name,
    onAddPredictions,
    onClickCallback,
    pageName,
    path,
    publicPrice,
    refetchCart,
    saleUnit,
    salesPackaging: priceAndStockSalesPackaging,
    sku,
    slug,
    source,
    splitPackaging: priceAndStockSplitPackaging,
    stockStatus,
    stocks,
    tableRowSize,
    weeeAmount,
  } = props;
  const splitPackaging = priceAndStockSplitPackaging ?? false;
  const salesPackaging = priceAndStockSalesPackaging ?? 1;
  const { user, isShowNetPrice } = useAuth();
  const { t } = useTranslation();
  const quantityFieldRef = useRef(null);
  const { getFlags } = useFlags(flags, stocks);
  const { addToCart, isLoading: isLoadingAddToCart } = useAddToContext();
  const { defaultCart } = useDefaultCartContext();
  const { modalIsOpen, onCloseModal } = useModalIAContext();

  const { offsetWidth } = document.body;

  let productLink = `${ROUTE_PRODUCT}/${path}/${slug}`;
  if (attributionToken) {
    productLink += `?${PREDICTIONS_KEY_PARAM}=${attributionToken}`;
  }

  const isDisabled = useMemo(
    () => ({
      addToCart:
        isLoadingAddToCart || disabled || !user?.functionality_accesses?.carts,
      addToWishlist:
        isLoadingAddToCart ||
        disabled ||
        !user?.functionality_accesses?.wishlists ||
        !user?.functionality_accesses?.wishlists_add,
      disabled,
      loading: isLoadingAddToCart,
    }),
    [disabled, isLoadingAddToCart, user],
  );

  const variantClassname = cn({
    'table-row': isTableRow,
    [`table-row--${tableRowSize}`]: isTableRow && tableRowSize,
    'table-row--without-net-price': !isShowNetPrice,
  });

  const onAddToCartSuccess = ({ data }) => {
    onAddPredictions({
      ...data[0],
      quantity: quantityFieldRef.current.value,
      product: {
        addToCartOptions: { options: { linkUrl: null } },
        brand,
        breadcrumb,
        className,
        configurations,
        configurator,
        customerPrice,
        discountPrice,
        exclusivity,
        flags,
        hasPrice, // withPriceAndStock
        hasStock, // withPriceAndStock
        id,
        image,
        imageSize,
        invoiceUnit,
        isFavorite,
        mainSku, // withPriceAndStock
        manufacturerSku,
        name,
        onAddPredictions,
        onClickCallback,
        path,
        publicPrice,
        refetchCart,
        saleUnit,
        salesPackaging,
        sku,
        slug,
        splitPackaging,
        stockStatus,
        stocks,
        weeeAmount,
      },
    });

    refetchCart();
  };

  const addToCartAction = () => {
    addToCart({
      items: [
        {
          quantity: quantityFieldRef.current.value,
          sku,
          name,
          id,
          brand,
          source,
        },
      ],
      cart: { ...defaultCart },
      onSuccessCallback: (data) => {
        const items = data?.items?.filter((item) => item.sku === sku) ?? [];
        if (items.length > 0) {
          onAddToCartSuccess({ data: items });
        }
      },
      options: { ...addToCartOptions },
    });
  };

  const handleChangeAddToCart = () => {
    if (offsetWidth < BREAKPOINTS.desktop) {
      addToCartAction();
    }

    return null;
  };

  const handleNavLinkClick = () => {
    if (modalIsOpen) {
      onCloseModal();
    }
    onClickCallback();
  };

  const adjustLongWordsWithSeparators = (text, maxLength = 10) =>
    text
      .split(' ')
      .map((word) => {
        if (word.length > maxLength) {
          return word.replace(/[-/]/g, ' - ');
        }
        return word;
      })
      .join(' ');

  return (
    <div
      className={cn('product', variantClassname)}
      data-qa-sku={sku}
      id={`product-${id}`}
      data-qa-id="product-list"
    >
      <div className={cn('product__flag-favorite', variantClassname)}>
        {Array.isArray(getFlags) && getFlags.length > 0 && (
          <Flags
            className={cn('product__flags', variantClassname)}
            flags={getFlags}
          />
        )}
        <div
          className={cn('product__favorite', variantClassname)}
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          <ToggleWishlist
            disabled={isDisabled.addToWishlist}
            id={path}
            isFavorite={isFavorite}
            sku={sku}
          />
        </div>
      </div>
      <NavLink
        className={cn('product__picture', variantClassname)}
        onClick={handleNavLinkClick}
        to={productLink}
      >
        <Picture
          alt={image?.alt || name}
          className="product__image"
          src={
            image?.url
              ? getImageBySize(image.url, PRODUCTS_TYPES_FORMATS.MOSAIC)
              : ''
          }
        >
          <Tag
            className="product__tag"
            label={t('ebat:entities:offers_category.title.exclusivity')}
            tag={exclusivity} // url image from product.exclusivity
          />
        </Picture>
      </NavLink>
      <div className={cn('product__description', variantClassname)}>
        <div className={cn('product__brand-name', variantClassname)}>
          {brand?.name && (
            <div
              className="product__brand"
              title={brand.name}
              data-qa-id="brand"
            >
              {brand.name}
            </div>
          )}
          <NavLink
            className={cn('product__name', variantClassname)}
            data-qa-id="product-name"
            onClick={handleNavLinkClick}
            title={name}
            to={productLink}
          >
            <Dotdotdot clamp={3}>
              {adjustLongWordsWithSeparators(name, 10)}
            </Dotdotdot>
          </NavLink>
          <div className="configurator-wrapper">
            {!isRelatedProduct && configurator && (
              <div className={cn('product__configurator', variantClassname)}>
                <ConfiguratorLink
                  link={productLink}
                  num={configurator?.num_configurations}
                  onClick={handleNavLinkClick}
                />
              </div>
            )}
          </div>
        </div>
        <NavLink
          className={cn('product__code', variantClassname)}
          data-qa-code={sku}
          data-qa-id="sku"
          onClick={handleNavLinkClick}
          to={productLink}
        >
          <span className="label">{t('common:entities:product.sku')}</span>
          <span className="value" data-qa-id="value">
            {sku}
          </span>
          {codeOldCompanySku && (
            <span className="sub-value">
              {' '}
              ({t('common:entities:product.old_sku')} : {codeOldCompanySku})
            </span>
          )}
        </NavLink>
        <NavLink
          className={cn('product__ref', variantClassname)}
          onClick={handleNavLinkClick}
          data-qa-id="manufacturer-sku"
          data-qa-manufacturer-sku={manufacturerSku}
          to={productLink}
        >
          <span className="label" data-qa-id="label">
            {t('common:entities:product.manufacturer_sku')}
          </span>
          <span className="manufacturer-sku__value" data-qa-id="value">
            {manufacturerSku}
          </span>
        </NavLink>
      </div>
      <div
        className={cn('product__stock-configurator', variantClassname)}
        data-qa-id="stock-configurator"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <Stocks
          categories={categories}
          className={cn('product__stock', variantClassname)}
          companyAvailability={companyAvailability}
          status={stockStatus}
          stocks={stocks}
          unit={saleUnit}
        />
        <div className={cn('product__configurator', variantClassname)}>
          {!isRelatedProduct && configurator && (
            <ConfiguratorLink
              link={productLink}
              num={configurator?.num_configurations}
              onClick={handleNavLinkClick}
            />
          )}
        </div>
      </div>
      <div className={cn('product__hr', variantClassname)} />
      <div className={cn('product__prices', variantClassname)}>
        {isShowNetPrice &&
          user?.functionality_accesses?.general_show_net_price && (
            <ProductPrice
              className={cn('product__net-price', variantClassname)}
              context="product-customer-price"
              discountPrice={discountPrice}
              invoiceUnit={invoiceUnit}
              label={t('common:entities:product.price.customer_price')}
              price={customerPrice}
              showEmptyLabel
              weeeAmount={weeeAmount}
            />
          )}
        <ProductPrice
          className={cn('product__public-price', variantClassname)}
          context="product-public-price"
          invoiceUnit={invoiceUnit}
          label={t('common:entities:product.price.public_price')}
          price={publicPrice}
          weeeAmount={weeeAmount}
        />
      </div>
      <div
        className={cn('product__add-to-cart', variantClassname)}
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <Quantity
          className={cn('product__quantity', variantClassname)}
          disabled={isDisabled.addToCart}
          id={id}
          isLoading={isDisabled.loading}
          max={
            pageName === PAGE_PRODUCTS_OFFERS &&
            !Number.isNaN(availableQuantity) &&
            !Number.isNaN(delayQuantity)
              ? availableQuantity + delayQuantity
              : undefined
          }
          onChange={handleChangeAddToCart}
          quantity={salesPackaging && !splitPackaging ? salesPackaging : 1}
          ref={quantityFieldRef}
          salesPackaging={salesPackaging}
          splitPackaging={splitPackaging}
          unit={saleUnit}
          updateOnChange={offsetWidth < BREAKPOINTS.desktop}
        />
        <div
          className={cn('product__cart-button', variantClassname)}
          data-qa-id="product-list-cart"
        >
          <ButtonLoading
            context="add-to-cart"
            disabled={isDisabled.addToCart}
            onClick={addToCartAction}
            title={t('common:messages:actions.add_to_cart')}
            value={sku}
            variant="primary"
            buttonSize="small"
          >
            <CartIcon className="svg-icon svg-icon-cart" />
          </ButtonLoading>
          <span className="unit">{saleUnit}</span>
        </div>
      </div>
    </div>
  );
};

Product.defaultProps = {
  ...ProductDefaultProps,
};

Product.propTypes = {
  ...ProductPropTypes,
};

export default withPriceAndStock(Product);
